/**
 * Projects — how the work sits against what was sold.
 *
 * Budget burn-down and retainer position, both cumulative rather than
 * period-scoped: a fixed fee is spent against the whole engagement, and a
 * retainer is judged on its trailing year. That is why this is a section of
 * its own rather than another panel under the period pager — everything here
 * would ignore that pager anyway.
 *
 * The place for the report types still to come.
 */

import { useMemo } from "react";

import { ProgressBar, card } from "@/components/atoms";
import { useProjects } from "@/hooks/useData";
import { useTimeEntries } from "@/hooks/useTime";
import { budgetStatus, hours, money } from "@/lib/timeRollup";

import { RetainerStrip } from "./parts";

export function ProjectsReport() {
  const { data: entries = [] } = useTimeEntries();
  const { data: projects = [] } = useProjects();

  /** Budgets read ALL entries, not a period — a burn-down is cumulative. */
  const budgets = useMemo(
    () => projects
      .map((p) => ({ project: p, status: budgetStatus(p, entries) }))
      .filter((r): r is { project: typeof r.project; status: NonNullable<typeof r.status> } => r.status !== null)
      .sort((a, b) => b.status.ratio - a.status.ratio),
    [projects, entries],
  );

  if (budgets.length === 0) {
    return (
      <div style={{ ...card, padding: "40px 24px", textAlign: "center" }}>
        <div style={{ fontSize: 15, fontWeight: "var(--fw-title)", color: "var(--ink)" }}>No budgets set</div>
        <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 6, maxWidth: 440, marginInline: "auto" }}>
          Give a project a fixed fee or a retainer allowance and its burn-down appears here.
          More report types will live on this tab.
        </div>
      </div>
    );
  }

  return (
    <>
      {budgets.length > 0 && (
        <div style={{ ...card, padding: 18, marginTop: 14 }}>
          <div style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)" }}>Budget</div>
          <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 3, marginBottom: 6 }}>
            Cumulative rather than period-scoped — a fixed fee is spent against the whole
            engagement, and a retainer always reads the current month.
          </div>
          {budgets.map(({ project, status }) => {
            const over = status.ratio > 1;
            const near = !over && status.ratio >= 0.85;
            return (
              <div key={project.id} style={{ padding: "11px 0", borderTop: "1px solid var(--tint2)" }}>
                <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13.5 }}>{project.name}</span>
                  <span style={{ fontSize: 11, color: "var(--muted2)" }}>
                    {status.kind === "retainer" ? `retainer · ${status.window}` : "fixed fee"}
                  </span>
                  <span className="mono" style={{
                    marginLeft: "auto", fontSize: 12.5,
                    color: over ? "var(--bad)" : near ? "var(--warn)" : "var(--ink3)",
                  }}>
                    {hours(status.usedSeconds)} / {status.budgetHours} h
                    {over && ` · ${hours(status.usedSeconds - status.budgetHours * 3600)} h over`}
                  </span>
                  {status.effectiveRate !== null && (
                    <span className="mono" title="Fee ÷ hours worked — what this time is really earning"
                      style={{ fontSize: 12, color: "var(--muted)" }}>
                      {money(status.effectiveRate, project.currency || "USD")}/h effective
                    </span>
                  )}
                </div>
                <div style={{ marginTop: 6 }}>
                  <ProgressBar
                    value={Math.min(100, status.ratio * 100)}
                    color={over ? "var(--bad)" : near ? "var(--warn)" : "var(--good)"}
                  />
                </div>
                {status.kind === "retainer" && (
                  <RetainerStrip project={project} entries={entries} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
